import type { MetricAggregation, MetricCardConfig } from "../types";

type Row = Record<string, unknown>;
type FilterMap = Record<string, (string | number | boolean | null)[]>;

/**
 * Apply a plotFilters-style filter map to a row array.
 * Each key is a column name; the row passes if its value is in the allowed set.
 * Null/empty filter arrays are treated as "no constraint".
 */
export function applyFilters(rows: Row[], filters: FilterMap): Row[] {
  const activeEntries = Object.entries(filters).filter(
    ([, values]) => values && values.length > 0,
  );
  if (activeEntries.length === 0) return rows;

  return rows.filter((row) =>
    activeEntries.every(([col, allowed]) => allowed.includes(row[col] as any)),
  );
}

/**
 * Aggregate an array of rows to a single number.
 * For "count"        — returns row count.
 * For "count-distinct" — returns unique non-null values in valueColumn.
 * For "sum/mean/min/max" — operates on numeric values in valueColumn.
 */
export function aggregateRows(
  rows: Row[],
  aggregation: MetricAggregation,
  valueColumn?: string,
): number {
  if (aggregation === "count") return rows.length;

  if (!valueColumn) return 0;

  if (aggregation === "count-distinct") {
    const seen = new Set(
      rows
        .map((r) => r[valueColumn])
        .filter((v) => v !== null && v !== undefined),
    );
    return seen.size;
  }

  const nums = rows
    .map((r) => {
      const v = r[valueColumn];
      return typeof v === "number" ? v : Number(v);
    })
    .filter((n) => !Number.isNaN(n));

  if (nums.length === 0) return 0;

  switch (aggregation) {
    case "sum":
      return nums.reduce((a, b) => a + b, 0);
    case "mean":
      return nums.reduce((a, b) => a + b, 0) / nums.length;
    case "min":
      return Math.min(...nums);
    case "max":
      return Math.max(...nums);
    default:
      return 0;
  }
}

/** Compute the primary (numerator) display value. */
export function computeMetricValue(
  rows: Row[],
  config: MetricCardConfig,
): number {
  return aggregateRows(rows, config.aggregation, config.valueColumn);
}

/** Compute numerator + denominator for donut style. */
export function computeDonutData(
  numeratorRows: Row[],
  denominatorRows: Row[] | null,
  config: MetricCardConfig,
): { numerator: number; denominator: number; ratio: number } {
  const numerator = aggregateRows(
    numeratorRows,
    config.aggregation,
    config.valueColumn,
  );

  let denominator: number;
  if (
    config.denominatorSource === "fixed" &&
    config.denominatorFixed !== undefined
  ) {
    denominator = config.denominatorFixed;
  } else if (denominatorRows !== null) {
    denominator = aggregateRows(
      denominatorRows,
      config.aggregation,
      config.valueColumn,
    );
  } else {
    denominator = numerator;
  }

  const ratio = denominator > 0 ? numerator / denominator : 0;
  return { numerator, denominator, ratio };
}

// ── Phase 3: sparkline / time series ──────────────────────────────────────

type BucketSize = "day" | "week" | "month";

function truncateDate(date: Date, bucket: BucketSize): string {
  const y = date.getFullYear();
  const m = date.getMonth();
  const d = date.getDate();
  if (bucket === "day")
    return `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  if (bucket === "week") {
    // ISO week start (Monday)
    const day = date.getDay(); // 0=Sun
    const diff = (day === 0 ? -6 : 1) - day;
    const mon = new Date(date);
    mon.setDate(d + diff);
    const my = mon.getFullYear();
    const mm = mon.getMonth();
    const md = mon.getDate();
    return `${my}-${String(mm + 1).padStart(2, "0")}-${String(md).padStart(2, "0")}`;
  }
  // month
  return `${y}-${String(m + 1).padStart(2, "0")}`;
}

export type TimeSeriesPoint = { date: string; value: number };

/**
 * Group rows by date bucket and aggregate each bucket.
 * `periodDays` — if provided, only include rows within the last N days.
 */
export function buildTimeSeries(
  rows: Row[],
  config: MetricCardConfig,
  periodDays?: number,
): TimeSeriesPoint[] {
  const {
    trendDateColumn,
    trendBucketSize = "week",
    aggregation,
    valueColumn,
  } = config;
  if (!trendDateColumn) return [];

  const now = new Date();
  const cutoff =
    periodDays != null
      ? new Date(now.getTime() - periodDays * 86_400_000)
      : null;

  const buckets = new Map<string, Row[]>();

  for (const row of rows) {
    const raw = row[trendDateColumn];
    if (raw == null) continue;
    const date = new Date(raw as string);
    if (isNaN(date.getTime())) continue;
    if (cutoff && date < cutoff) continue;
    const key = truncateDate(date, trendBucketSize);
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key)!.push(row);
  }

  return Array.from(buckets.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([date, bucketRows]) => ({
      date,
      value: aggregateRows(bucketRows, aggregation, valueColumn),
    }));
}

/** Compute % change between the last period and the one before it. */
export function computeDelta(
  rows: Row[],
  config: MetricCardConfig,
  periodDays: number,
): { current: number; previous: number; delta: number; pct: number } | null {
  const { trendDateColumn, aggregation, valueColumn } = config;
  if (!trendDateColumn) return null;

  const now = new Date();
  const cutoffCurrent = new Date(now.getTime() - periodDays * 86_400_000);
  const cutoffPrevious = new Date(now.getTime() - 2 * periodDays * 86_400_000);

  const currentRows = rows.filter((r) => {
    const d = new Date(r[trendDateColumn] as string);
    return !isNaN(d.getTime()) && d >= cutoffCurrent;
  });
  const previousRows = rows.filter((r) => {
    const d = new Date(r[trendDateColumn] as string);
    return !isNaN(d.getTime()) && d >= cutoffPrevious && d < cutoffCurrent;
  });

  const current = aggregateRows(currentRows, aggregation, valueColumn);
  const previous = aggregateRows(previousRows, aggregation, valueColumn);
  const delta = current - previous;
  const pct = previous !== 0 ? (delta / previous) * 100 : 0;
  return { current, previous, delta, pct };
}

/** Format a number for display — up to 1 decimal for floats, integers stay clean. */
export function formatMetricValue(value: number): string {
  if (!Number.isFinite(value)) return "—";
  if (Number.isInteger(value)) return value.toLocaleString();
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  });
}

/** Resolve threshold colour class given config and current value. */
export function resolveThresholdColor(
  value: number,
  config: MetricCardConfig,
): "danger" | "warn" | "default" {
  const {
    thresholdCritical,
    thresholdWarn,
    thresholdDirection = "above",
  } = config;
  const exceeds = (threshold: number) =>
    thresholdDirection === "above" ? value > threshold : value < threshold;

  if (thresholdCritical !== undefined && exceeds(thresholdCritical))
    return "danger";
  if (thresholdWarn !== undefined && exceeds(thresholdWarn)) return "warn";
  return "default";
}
